import log from 'loglevel';
import { watch } from 'vue';
import { defineStore } from 'pinia';
import { useSnackbar } from '@/composables/useSnackbar';

import { useUserStore } from './UserStore';
import { useAppConfigStore } from './AppConfigStore';
import { OfferService } from '@/api/services/offerService';

import { Paging } from '@/interfaces/Paging';
import { Offer } from '@/interfaces/Offers/Offer';
import { OfferCategory } from '@/interfaces/OfferCategory';
import { logOfferStartEvent } from "@/api/services/AnalyticsTracker";
import { OffersParameters } from '@/interfaces/Offers/OffersParameters';
import { OfferSortOptions } from '@/interfaces/Offers/OfferSortOptions';
import { CategoryAndOffers } from '@/interfaces/Offers/CategoryAndOffers';
import { TabbedOffersResponse } from '@/interfaces/Offers/TabbedOffersResponse';
import { HomeFeedOffersResponse } from '@/interfaces/Offers/HomeFeedOffersResponse';



export const OfferStore = defineStore('offerStore', {
    state: () => ({


        dailyPicksOffers: [] as Offer[],
        dailyNewOffers: [] as Offer[],
        loadingHomeFeedOffers: false,
        offers: [] as Offer[],
        categoriesAndOffers: [] as CategoryAndOffers[],
        loadingCategoriesAndOffers: false,
        offersPaging: {
            count: 60,
            offset: 0,
            page: 1,
            totalPages: 1,
        } as Paging,

        offerSortOptions: [] as OfferSortOptions[],
        selectedOffer: {} as Offer,


        offersParameters: {

            platforms: 15,
            id: undefined,
            search_query: '',
            limit: undefined,
            network: undefined,
            category: undefined,
            test_mode: undefined,
            sort_by: 'recommended',
            earn_option_id: undefined,
            load_live_offers: undefined,

        } as OffersParameters,
        loadingOffers: false,



    }),




    getters: {


        categories(): OfferCategory[] {
            const allButton = {
                id: undefined,
                title: "All",
                selected: this.offersParameters.category === undefined,
            };
            const hardcodedCategories = [
                { id: 2, title: 'Discover Apps' },
                { id: 4, title: 'Surveys' },
                { id: 6, title: 'Complete Offers' },
            ];
            return [allButton, ...hardcodedCategories].map((category) => ({
                ...category,
                selected: category.id === this.offersParameters.category,
            }));
        },



    },




    actions: {



        async initialize() {
            // The plugin will manage the isInitialized and isInitializing flags
            // This method will only be called once by the plugin
            try {
                await this.getAllOffers();
                await this.getOffersForHomeFeed();
                await this.getTabbedOffers();
                this.setupWatchers();
                return true;
            } catch (error) {
                console.error('Error initializing OfferStore:', error);
                return false;
            }
        },



        async getAllOffers() {
            this.loadingOffers = true;
            try {
                const offersAndPaging = await OfferService.getAllOffers({ ...this.offersParameters, ...this.offersPaging });
                this.offers = offersAndPaging?.data ?? [];
                // todo: when we get new offset from the server, we should update the page number
                this.offersPaging.totalPages = offersAndPaging?.paging?.totalPages ?? 1;
                this.offersPaging.page = Math.ceil(this.offersPaging.offset / this.offersPaging.count) + 1;
                this.loadingOffers = false;
            } catch (error) {
                console.log(error);
                // Don't throw the error, just log it
                log.error('Failed to get all offers:', error);
            }
            finally {
                this.loadingOffers = false;
            }
        },



        async getOffersForHomeFeed() {
            this.loadingHomeFeedOffers = true;
            try {
                const response: HomeFeedOffersResponse = await OfferService.getDailyPicksOffers({ platforms: this.offersParameters.platforms });
                this.dailyPicksOffers = response?.daily_offers ?? [];
                this.dailyNewOffers = response?.daily_new_offers ?? [];
            } catch (error) {
                log.error('Failed to get offers for home feed:', error);
                // Don't throw the error, just log it
            }
            finally {
                this.loadingHomeFeedOffers = false;
            }
        },



        async getTabbedOffers() {
            this.loadingCategoriesAndOffers = true;
            try {
                const response: TabbedOffersResponse = await OfferService.getTabbedOffers({ platforms: this.offersParameters.platforms });
                this.categoriesAndOffers = response?.data ?? [];
            } catch (error) {
                log.error('Failed to get tabbed offers:', error);
                // Don't throw the error, just log it
            }
            finally {
                this.loadingCategoriesAndOffers = false;
            }
        },



        // TODO: possibly handle errors in watchers using try catch.
        setupWatchers() {
            const appConfigStore = useAppConfigStore();
            watch(() => this.offersPaging.page, (newValue: number) => {
                this.offersPaging.offset = (newValue - 1) * this.offersPaging.count;
                this.getAllOffers();
            });
            watch(() => this.offersParameters, () => {
                if (this.offersPaging.page === 1)
                    this.getAllOffers();
                this.setPage(1);
            }, { deep: true });
            watch(() => appConfigStore.platforms, (newValue: number) => {
                this.offersParameters.platforms = newValue;
                this.getTabbedOffers();
                this.getOffersForHomeFeed();
            });
        },



        setParams(params: Partial<OffersParameters>) {
            this.offersParameters = { ...this.offersParameters, ...params };
        },



        async startOffer() {
            const { showSnackbar } = useSnackbar();
            const userStore = useUserStore();
            try {
                const offer = this.selectedOffer;
                if (!offer) throw new Error(`Offer not found, Try selecting the offer again.`);
                await OfferService.startOffer(offer.id, offer.variant_id);
                logOfferStartEvent(offer, userStore.user.id);
            } catch (error) {
                showSnackbar({
                    message: 'Error starting offer.',
                    color: 'error',
                });
            }
        },



        clearStoreData() {
            this.$reset();
        },



        selectCategory(categoryId: number | undefined) {
            this.offersParameters.category = categoryId;
        },




        selectOffer(offer: Offer) {
            this.selectedOffer = offer;
        },



        async reportSelectedOffer(reasonCode: string) {
            await OfferService.reportOffer(this.selectedOffer.id, reasonCode);
        },



        setPage(page: number) {
            this.offersPaging.page = page;
        },
    }
});
